import { useState } from 'react';
import { api, useApi } from '../api';
import { Card, EmptyState, ErrorBanner, Loading } from '../components/ui';
import { useAviso } from '../avisos';
import { useT } from '../i18n';

/**
 * El cajón de herramientas de afuera: descargadores, editores, lo que haga falta.
 *
 * ── Por qué no son leads ──────────────────────────────────────────────────────
 * Un lead es una cuenta ajena que se sigue y se mide: tiene seguidores, capturas periódicas y
 * una razón para volver a mirarla. Esto es un enlace con nombre. Compartir pantalla obligaría a
 * que la mitad de las columnas estuvieran siempre vacías, y a que «agregar» significara dos
 * cosas distintas según qué se esté agregando.
 *
 * ── Por qué se guarda en los ajustes y no en una tabla ────────────────────────
 * Porque no hay ninguna pregunta que hacerle a estos datos salvo «mostrámelos». Una tabla
 * costaría una migración, un repositorio y un alcance por dueño para guardar lo que cabe en un
 * array — y los ajustes ya son de cada persona, que era lo único que hacía falta.
 *
 * ── Por qué los enlaces salen con `noopener` ──────────────────────────────────
 * Porque son direcciones de afuera que escribe quien usa el panel. Sin él, la página que se
 * abre puede reescribir la pestaña de origen desde `window.opener` — que es la nuestra, con la
 * sesión abierta.
 */
type Herramienta = { id: string; nombre: string; url: string; nota: string | null };

export function Descargadores() {
  const { t } = useT();
  const aviso = useAviso();
  const state = useApi<{ settings: { herramientas?: Herramienta[] } }>('/api/settings');
  const [guardando, setGuardando] = useState(false);
  const [nombre, setNombre] = useState('');
  const [url, setUrl] = useState('');
  const [nota, setNota] = useState('');

  if (state.error) return <ErrorBanner error={state.error} onRetry={state.reload} />;
  if (!state.data) return <Loading label={t('herr.cargando')} />;

  const herramientas = state.data.settings.herramientas ?? [];

  /** Guarda la lista entera. Devuelve si salió bien, para no vaciar el formulario si falló. */
  const guardar = async (lista: Herramienta[]): Promise<boolean> => {
    setGuardando(true);
    try {
      await api.patch('/api/settings', { herramientas: lista });
      state.reload();
      return true;
    } catch {
      /* El aviso lo pone el cliente de API, que muestra todos los errores en un solo sitio. */
      return false;
    } finally {
      setGuardando(false);
    }
  };

  const agregar = async (): Promise<void> => {
    if (nombre.trim().length === 0 || url.trim().length === 0) return;

    /*
      La dirección se manda tal cual y la limpia el servidor.

      ── Por qué no se valida acá ──────────────────────────────────────────────
      Porque el panel no importa de `@vcos/domain` a propósito: sólo habla con la API. Copiar la
      regla acá sería una segunda versión de la misma comprobación, y la que se relaja siempre es
      la que nadie mira. El servidor completa el esquema cuando falta y rechaza lo que no sea
      http o https — ver `normalizarEnlaceDeHerramienta`.

      Si la rechaza, el aviso lo pone el cliente de API, que muestra TODOS los errores sin que
      cada pantalla se acuerde. Por eso acá no hay mensaje propio.
    */
    const guardado = await guardar([
      ...herramientas,
      {
        id: crypto.randomUUID(),
        nombre: nombre.trim(),
        url: url.trim(),
        nota: nota.trim() || null,
      },
    ]);
    if (!guardado) return;

    setNombre('');
    setUrl('');
    setNota('');
    aviso.ok(t('herr.agregada'));
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-semibold">{t('herr.titulo')}</h1>
        <p className="mt-0.5 text-sm text-[var(--color-ink-muted)]">{t('herr.bajada')}</p>
      </div>

      <Card title={t('herr.agregar')}>
        <div className="flex flex-wrap items-end gap-3">
          <label className="block">
            <span className="text-xs font-medium">{t('herr.nombre')}</span>
            <input
              value={nombre}
              onChange={(event) => setNombre(event.target.value)}
              maxLength={80}
              placeholder={t('herr.nombreEjemplo')}
              className="mt-1 w-48 rounded-lg border border-[var(--color-border-subtle)] bg-transparent px-3 py-2 text-sm"
            />
          </label>
          <label className="block min-w-0 flex-1">
            <span className="text-xs font-medium">{t('herr.url')}</span>
            <input
              value={url}
              onChange={(event) => setUrl(event.target.value)}
              placeholder="ejemplo.com/descargador"
              className="mt-1 w-full rounded-lg border border-[var(--color-border-subtle)] bg-transparent px-3 py-2 text-sm"
            />
          </label>
          <label className="block">
            <span className="text-xs font-medium">{t('herr.nota')}</span>
            <input
              value={nota}
              onChange={(event) => setNota(event.target.value)}
              maxLength={200}
              placeholder={t('herr.notaEjemplo')}
              className="mt-1 w-56 rounded-lg border border-[var(--color-border-subtle)] bg-transparent px-3 py-2 text-sm"
            />
          </label>
          <button
            type="button"
            disabled={guardando || nombre.trim().length === 0 || url.trim().length === 0}
            onClick={() => void agregar()}
            className="rounded-lg border border-[var(--color-border-subtle)] px-3 py-2 text-sm hover:border-[var(--color-accent)] disabled:opacity-40"
          >
            {t(guardando ? 'herr.guardando' : 'herr.agregarBoton')}
          </button>
        </div>
      </Card>

      {herramientas.length === 0 ? (
        <EmptyState title={t('herr.ninguna')} description={t('herr.ningunaQue')} />
      ) : (
        <Card title={t('herr.listaTitulo')}>
          <ul className="divide-y divide-[var(--color-border-subtle)]">
            {herramientas.map((herramienta) => (
              <li key={herramienta.id} className="flex flex-wrap items-center gap-3 py-2.5">
                <div className="min-w-0 flex-1">
                  <a
                    href={herramienta.url}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="text-sm font-medium hover:text-[var(--color-accent)]"
                  >
                    {herramienta.nombre}
                  </a>
                  <p className="truncate text-xs text-[var(--color-ink-muted)]">
                    {herramienta.nota ? `${herramienta.nota} · ` : ''}
                    {herramienta.url}
                  </p>
                </div>
                <button
                  type="button"
                  disabled={guardando}
                  onClick={() =>
                    void guardar(herramientas.filter((otra) => otra.id !== herramienta.id))
                  }
                  className="shrink-0 rounded-lg border border-[var(--color-border-subtle)] px-2.5 py-1 text-xs hover:border-[var(--color-danger)] hover:text-[var(--color-danger)] disabled:opacity-40"
                >
                  {t('herr.quitar')}
                </button>
              </li>
            ))}
          </ul>
        </Card>
      )}
    </div>
  );
}
